// src/components/RequestGate.js
//
// Fan-inbox kit: unread indicators + the message-request blur gate.
//
//   isUnread(conversation)      rail helper — lime dot condition
//   markRead(conversationId)    call on thread open
//   RequestRowOverlay           blurs a rail row's name+preview, lock chip
//   LockedThread                full-thread gate: blurred bubbles behind a
//                               membership card with live ETH quote
//
// THE BLUR IS A SALES TOOL, and as of the request-gate hardening it is
// REAL: chat-api withholds the body, the media and the rail preview for a
// blurred thread and sends only shape — how many messages, roughly how
// long, whether there is an image. So a leak here is not a privacy
// incident, it is a LOST SALE: text that reaches the browser satisfies the
// curiosity for free and the fan never buys.
//
// Which is why these bubbles are drawn from the server's `body_len` and
// `has_media` rather than from three hardcoded bars: a believable shape is
// the thing that makes someone want to see through it.
//
// AUTH FOOTGUN: src/context/AuthContext.js (SIWE, singular).

import { useCallback, useEffect, useState } from 'react';
import { Lock, Loader2, Sparkles, MessageSquare } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useSiweAuth } from '../context/AuthContext';
import { payThenVerify } from './ChatEconomy';

// ---------------------------------------------------------------------
// UNREAD
// ---------------------------------------------------------------------
export function isUnread(conversation) {
  const lastAt = conversation.last_message_at ?? conversation.lastMessageAt ?? conversation.time;
  const lastSender = conversation.last_message_sender ?? conversation.lastMessageSender;
  const readAt = conversation.user_last_read_at ?? conversation.userLastReadAt;
  if (!lastAt) return false;
  if (lastSender && lastSender !== 'creator') return false; // own messages never unread
  return !readAt || new Date(lastAt) > new Date(readAt);
}

export async function markRead(conversationId) {
  // RLS is enforced on conversations — the write goes through the
  // SIWE-authed chat-api (sets user_last_read_at + zeroes the counter).
  try {
    const { authService } = await import('../services/AuthService');
    const wallet = authService.getSessionWallet();
    const token = wallet ? authService.getToken(wallet) : null;
    if (!token) return;
    await supabase.functions.invoke('chat-api', {
      body: { action: 'read', conversationId },
      headers: { 'X-Lunexx-Auth': `Bearer ${token}` },
    });
  } catch (e) { console.warn('[unread] markRead failed:', e); }
}

/** The lime dot itself — drop next to the name in the rail row. */
export function UnreadDot() {
  return (
    <span className="ml-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-[#A3E635] shadow-[0_0_8px_rgba(163,230,53,0.7)]" />
  );
}


// ---------------------------------------------------------------------
// SERVER BRIDGE
// ---------------------------------------------------------------------
async function bridge(authToken, body) {
  const { data, error } = await supabase.functions.invoke('unlock-message', {
    body,
    headers: { 'X-Lunexx-Auth': `Bearer ${authToken}` },
  });
  if (error) {
    let message = error.message || 'request failed';
    if (error.context?.json) {
      try { const d = await error.context.json(); if (d?.error) message = d.error; } catch { /* noop */ }
    }
    throw new Error(message);
  }
  return data;
}

// ---------------------------------------------------------------------
// RAIL: blurred request row
// ---------------------------------------------------------------------
// Wrap the row's TEXT column (name + preview) — avatar stays visible,
// which reads as "someone interesting wants to reach you":
//
//   {convo.is_request
//     ? <RequestRowOverlay>{textColumn}</RequestRowOverlay>
//     : textColumn}
export function RequestRowOverlay({ children }) {
  return (
    <div className="relative min-w-0 flex-1">
      <div className="pointer-events-none select-none blur-[6px] brightness-75">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center gap-1.5">
        <Lock size={11} className="text-white/50" />
        <span className="text-[11px] font-medium tracking-wide text-white/50">
          New message request
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// THREAD: full lock gate
// ---------------------------------------------------------------------
// Render INSTEAD of the message list when the active conversation has
// is_request. `creator` needs { id, name, avatar_url, wallet_address }.
// onUnlocked() -> clear is_request locally + refetch conversation.
// XPass-first economy: unblurring = holding the creator's XPass. The
// gate REVEALS the creator fully (an XPass is an investment decision —
// nobody buys an asset from a smudge; the mystery that sells is the
// MESSAGE, which stays hidden) and redirects to their market tab.
// The membership machinery above stays exported but dormant — it
// returns as Chat Pass plumbing.
export function LockedThread({
  creator, conversationId, onUnlocked,
  /** Redacted rows from chat-api: { id, body_len, has_media, ... }. The
   *  bodies are NOT here — that is the point. */
  messages = [],
  /** Which products open this thread, straight from the server's refusal
   *  so the CTA can never drift from what actually unlocks. */
  unlockWith = ['founding_card', 'chat_pass'],
}) {
  const { ensureAuth } = useSiweAuth();
  const [checking, setChecking] = useState(false);
  const [buyingPass, setBuyingPass] = useState(false);
  const [passErr, setPassErr] = useState(null);

  // Bubble geometry from the withheld lengths. Clamped so a one-word
  // message is still visible and an essay does not overflow the column.
  const lockedShapes = (messages ?? []).slice(-4).map((m) => {
    const len = Number(m?.body_len) || 0;
    const rows = Math.min(4, Math.max(1, Math.ceil(len / 38)));
    return {
      id: m.id,
      h: m.has_media ? 120 : 20 + rows * 20,
      w: `${Math.min(78, Math.max(28, 24 + len * 1.1))}%`,
    };
  });
  const msgCount = (messages ?? []).length;
  const hasPhoto = (messages ?? []).some((m) => m?.has_media);
  const navigate = typeof window !== 'undefined'
    ? (path) => { window.location.assign(path); }
    : () => { };

  // THE UNLOCK LOOP: verify the fan's live relationship (XPass / room
  // ticket) server-side and dissolve the gate. Runs when the locked
  // thread opens AND every time the window regains focus — so buying the
  // pass on the market tab unblurs the thread the moment they return.
  const checkUnlock = useCallback(async () => {
    if (!conversationId || checking) return;
    setChecking(true);
    try {
      const token = await ensureAuth();
      const res = await bridge(token, { action: 'request_unlock', conversationId });
      if (res?.unlocked) onUnlocked?.();
    } catch (e) {
      if (!e?.cancelled) console.warn('[request-gate] unlock check failed:', e);
    } finally {
      setChecking(false);
    }
  }, [conversationId, checking, ensureAuth, onUnlocked]);

  // BUY THE CHEAPER DOOR, without leaving the thread. Same rail as
  // ChatEconomy's Chat Pass: quote -> pay the platform wallet -> verify.
  // chat_pass_buy dissolves is_request server-side on success, so the
  // unlock check below simply confirms it.
  const buyChatPass = useCallback(async () => {
    if (buyingPass) return;
    setBuyingPass(true); setPassErr(null);
    try {
      const token = await ensureAuth();
      const quote = await bridge(token, { action: 'chat_pass_quote' });
      const platformWallet = process.env.REACT_APP_PLATFORM_WALLET;
      if (!platformWallet) throw new Error('Payments are not configured');
      // payThenVerify carries the tx_not_found backoff — without it the
      // fan pays and the unlock never lands while the tx propagates.
      await payThenVerify(ensureAuth, platformWallet, quote.eth, {
        action: 'chat_pass_buy', creatorId: creator?.id ?? creator?.creator_id,
      });
      onUnlocked?.();
    } catch (e) {
      setPassErr(e?.message ?? 'Could not complete the purchase');
    } finally {
      setBuyingPass(false);
    }
  }, [buyingPass, ensureAuth, creator, onUnlocked]);

  useEffect(() => {
    checkUnlock();
    const onFocus = () => checkUnlock();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  return (
    <div className="relative flex flex-1 flex-col items-center justify-center overflow-hidden px-6">
      {/* The hidden messages, drawn from the SERVER'S shape. chat-api
          withholds the body but sends body_len and has_media, so these are
          the real number of real-length bubbles — which is what makes the
          blur tempting rather than obviously decorative. Falls back to a
          generic suggestion if the shape has not loaded. */}
      <div className="pointer-events-none absolute inset-0 flex flex-col justify-center gap-3 px-10 opacity-40 blur-[7px]">
        {lockedShapes.length ? lockedShapes.map((m) => (
          <div
            key={m.id}
            className="rounded-2xl bg-[#1E1E1E]"
            style={{ height: m.h, width: m.w }}
          />
        )) : (
          <>
            <div className="h-10 w-2/3 rounded-2xl bg-[#1E1E1E]" />
            <div className="h-10 w-1/2 rounded-2xl bg-[#1E1E1E]" />
            <div className="h-16 w-3/5 rounded-2xl bg-[#1E1E1E]" />
          </>
        )}
      </div>

      <div className="relative z-10 w-full max-w-sm rounded-[28px] border border-white/10 bg-[#141414]/95 p-8 text-center backdrop-blur">
        {creator.avatar_url && (
          <img src={creator.avatar_url} alt=""
            className="mx-auto mb-4 h-16 w-16 rounded-full border border-white/10 object-cover" />
        )}
        <h3 className="mb-1 text-lg font-bold tracking-tight text-white">
          {creator.name} sent you {msgCount > 1 ? `${msgCount} messages` : 'a message'}
        </h3>
        <p className="mb-6 text-sm leading-relaxed text-white/45">
          {hasPhoto
            ? 'There\u2019s a photo in here. Unlock the conversation to see it.'
            : 'Unlock the conversation to read it.'}
        </p>

        {/* OWN — the permanent unlock, and the bigger sale. */}
        {unlockWith.includes('founding_card') && (
          <button
            onClick={() => navigate(`/kol/${creator.handle}?tab=market`)}
            className="mb-2 flex w-full items-center justify-center gap-2 rounded-full bg-white py-3.5 text-sm font-medium text-black transition hover:bg-white/90 active:scale-[0.98]"
          >
            <Sparkles size={15} /> Get {creator.name?.split(' ')[0] ?? 'their'}&apos;s founding pass
          </button>
        )}

        {/* RENT — the cheaper door. Offered second on purpose: the card
            unlocks every message forever, the pass unlocks this creator. */}
        {unlockWith.includes('chat_pass') && (
          <button
            onClick={buyChatPass}
            disabled={buyingPass}
            className="mb-3 flex w-full items-center justify-center gap-2 rounded-full border border-white/15 py-3.5 text-sm font-medium text-white transition hover:border-white/30 disabled:opacity-50"
          >
            {buyingPass ? <Loader2 size={15} className="animate-spin" /> : <MessageSquare size={15} />}
            Chat Pass &middot; unlock this chat
          </button>
        )}

        {passErr && <p className="mb-2 text-[11px] text-red-400/90">{passErr}</p>}

        <p className="text-[11px] text-white/30">
          The founding pass unlocks every message, member chat and
          holder-only rooms &mdash; forever.
        </p>
      </div>
    </div>
  );
}